import * as Sentry from "@sentry/react";
import { HTTPError } from "ky";
import { createContext, useContext, useEffect, useMemo } from "react";
import {
  AppConfiguration,
  useAppConfiguration,
} from "./api/app-configuration.api";
import { Button } from "./components/generic/Button";
import { ErrorPage } from "./components/layout/ErrorPage";
import { Loader } from "./components/layout/Loader";
import { NotAuthenticated } from "./components/layout/NotAuthenticated";
import {
  ImpersonationSessionExpiredError,
  useAuthState,
} from "./services/auth.service";
import { Feature, User } from "./types/auth";
import lassoUrls from "./utils/lasso-urls";

interface AuthState {
  user: User;
  configuration: AppConfiguration;
  features: Feature[];
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    data: authData,
    error: authError,
    isPending,
    isError,
    refetch: refetchAuth,
  } = useAuthState();

  const {
    data: configData,
    isPending: isConfigPending,
    isError: isConfigError,
    refetch: refetchConfig,
  } = useAppConfiguration();

  const isAuthorized = useMemo(
    () =>
      !!authData?.isAuthorized ||
      (!!configData?.subscription.trial && !!configData.subscription.expired),
    [authData, configData],
  );

  useEffect(() => {
    if (!authData) return;

    if (isAuthorized) {
      // Identify and track user
      Sentry.setUser({ email: authData.user.email, id: authData.user.id });
      Sentry.setContext("organization", {
        plan: authData.features?.includes(Feature.LassoSalesPro)
          ? "Pro"
          : "Standard",
        id: authData.user.organization.lassoOrganizationId,
        lassoId: authData.user.organization.lassoId,
        name: authData.user.organization.name,
      });
    }
  }, [authData, isAuthorized]);

  // Must come before the 401 redirect below - sending the browser to the login page would sign it
  // in as the impersonator rather than the user we were impersonating.
  if (isError && authError instanceof ImpersonationSessionExpiredError) {
    return (
      <div
        id="main-scrollable-area"
        className="text-theme-on-background relative flex min-h-full flex-col gap-y-3"
      >
        <ErrorPage
          icon="cloud-alert"
          title="Din session er udløbet"
          message="Du kontrollerede en anden bruger, og den session er nu udløbet. Luk fanen, og åbn Lasso Sales igen fra Lasso Portal."
        />
      </div>
    );
  }

  if (
    isError &&
    authError instanceof HTTPError &&
    authError.response.status === 401
  ) {
    location.href = `${lassoUrls.getAppsUrl()}/login?returnUrl=${encodeURIComponent(
      location.href,
    )}`;
    return null;
  }

  if (authData && configData && !isAuthorized) {
    return (
      <div
        id="main-scrollable-area"
        className="text-theme-on-background relative flex min-h-full flex-col gap-y-3 p-0"
      >
        <NotAuthenticated />
      </div>
    );
  }

  if (isError || isConfigError) {
    return (
      <div
        id="main-scrollable-area"
        className="text-theme-on-background relative flex min-h-full flex-col gap-y-3"
      >
        <ErrorPage
          icon="cloud-alert"
          title="Der opstod en fejl"
          message="Der opstod desværre en uventet fejl. Vores teknikere har fået besked og arbejder på sagen"
          actions={
            <>
              <Button
                intent="primary"
                onClick={() => {
                  if (isError) {
                    refetchAuth();
                  }

                  if (isConfigError) {
                    refetchConfig();
                  }
                }}
                className="mt-4"
              >
                Prøv igen
              </Button>
            </>
          }
        />
      </div>
    );
  }

  if (isPending || isConfigPending) {
    return (
      <div className="bg-background fixed z-50 flex size-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!authData) {
    return (
      <div
        id="main-scrollable-area"
        className="text-theme-on-background relative flex min-h-full flex-col gap-y-3 p-0"
      >
        <ErrorPage
          title="Du bliver nu logget ud"
          message="Du bliver sendt til Lasso Insights om lidt. Hvis du ikke bliver sendt automatisk, kan du klikke på knappen nedenfor."
          actions={
            <Button
              intent="secondary"
              onPress={() => {
                location.href =
                  lassoUrls.getAppsUrl() +
                  `/logout?returnUrl=` +
                  encodeURIComponent(document.location.href);
              }}
            >
              Gå til Lasso Insights
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user: authData.user,
        features: authData.features,
        configuration: configData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
